"use client"

import { useEffect, useRef, useState } from "react"
import cx from "classnames"
import {
  useBaseBetslip,
  useChain,
  useDetailedBetslip,
  useBet,
} from "@azuro-org/sdk"
import { type TransactionReceipt, type Address } from "viem"
import { Message } from "@locmod/intl"
import { useAccount } from "@azuro-org/sdk-social-aa-connector"
import { openModal } from "@locmod/modal"
import localStorage from "@locmod/local-storage"
import {
  constants,
  isUserRejectedRequestError,
  updateDiscordChannel,
  toLocaleString,
} from "helpers"

import { Icon } from "components/ui"
import { buttonMessages } from "components/inputs"

import messages from "./messages"
import { usePublicClient } from "wagmi"
import { sendGTMEvent } from "@next/third-parties/google"

import { ConnectorName, useWallet } from "wallet"
import { useUserContext } from "src/contexts/user"
import { useBalanceContext } from "src/contexts/BalanceContext"

type BetButtonProps = {
  isEnoughBalance: boolean
  isBalanceFetching: boolean
}

const BetButton: React.FC<BetButtonProps> = ({
  isEnoughBalance,
  isBalanceFetching,
}) => {
  const [isAllowanceInitiated, setIsAllowanceInitiated] = useState(false)
  const { refetchBalances } = useBalanceContext()

  const { chainId, chain } = useAccount()
  const { connector, account: address } = useWallet()
  const { currentUser } = useUserContext()
  const { betToken } = useChain()
  const publicClient = usePublicClient()
  const { items, clear } = useBaseBetslip()
  const {
    betAmount,
    odds,
    totalOdds,
    selectedFreebet,
    isBetAllowed,
    isOddsFetching,
    isStatesFetching,
    isMaxBetFetching,
  } = useDetailedBetslip()

  const totalOddsRef = useRef(totalOdds)

  if (!isOddsFetching) {
    totalOddsRef.current = totalOdds
  }
  const slippage = +(
    (localStorage.getItem(constants.localStorageKeys.slippage) as string) ||
    constants.defaultSlippage
  )
  const diff =
    selectedFreebet && selectedFreebet.params.isSponsoredBetReturnable
      ? +selectedFreebet.amount
      : 0
  const possibleWin = toLocaleString(totalOddsRef.current * +betAmount - diff, {
    digits: 2,
  })

  const getTotalPossibleWin = () => {
    if (!items.length) {
      return 0
    }

    // Calculate the sum of all possible wins (odds * batchBetAmount)
    let totalWin = 0

    items.forEach(item => {
      const { conditionId, outcomeId } = item
      const itemOdds = odds?.[`${conditionId}-${outcomeId}`] || 0
      const itemBatchAmount = betAmount
      // batchBetAmounts[`${conditionId}-${outcomeId}`] || '0'
      const possibleWin = itemOdds * +itemBatchAmount

      totalWin += possibleWin
    })

    return totalWin
  }

  const checkDifferentGames = (items: AzuroSDK.BetslipItem[]) => {
    if (items.length <= 1) {
      return false
    }

    // Get all gameIds
    const gameIds = items.map(({ gameId }) => gameId)

    // Check if there are any duplicate gameIds (any same-game selections)
    return new Set(gameIds).size < gameIds.length
  }

  const getHighestPossibleWin = () => {
    if (!isDifferentGames || !items.length) {
      return 0
    }

    // Group items by gameId
    const gameGroups: Record<string, AzuroSDK.BetslipItem[]> = {}
    items.forEach(item => {
      const gameId = item.gameId
      if (!gameGroups[gameId]) {
        gameGroups[gameId] = []
      }
      gameGroups[gameId].push(item)
    })

    let totalHighestWin = 0

    // For each group of same-game selections, find the highest win
    Object.values(gameGroups).forEach(gameItems => {
      // If there's only one selection for this game, add its win to the total
      if (gameItems.length === 1) {
        const item = gameItems[0]
        const { conditionId, outcomeId } = item
        const itemOdds = odds?.[`${conditionId}-${outcomeId}`] || 0
        const itemBatchAmount = betAmount
        // batchBetAmounts[`${conditionId}-${outcomeId}`] || '0'
        totalHighestWin += itemOdds * +itemBatchAmount
        return
      }

      // For multiple selections from the same game, find the highest win
      let highestWin = 0
      gameItems.forEach(item => {
        const { conditionId, outcomeId } = item
        const itemOdds = odds?.[`${conditionId}-${outcomeId}`] || 0
        const itemBatchAmount = betAmount
        // batchBetAmounts[`${conditionId}-${outcomeId}`] || '0'
        const possibleWin = itemOdds * +itemBatchAmount

        if (possibleWin > highestWin) {
          highestWin = possibleWin
        }
      })

      totalHighestWin += highestWin
    })

    return totalHighestWin
  }

  const isDifferentGames = checkDifferentGames(items)

  const {
    submit,
    approveTx,
    betTx,
    isRelayerFeeLoading,
    isAllowanceLoading,
    isApproveRequired,
  } = useBet({
    // betAmount: isBatch ? batchBetAmounts : betAmount,
    betAmount,
    slippage,
    affiliate: process.env.NEXT_PUBLIC_AFFILIATE_ADDRESS as Address,
    selections: items,
    odds,
    totalOdds,
    freebet: selectedFreebet,
    EIP712Attention:
      "By signing this transaction, I agree to place a bet for a live event on DGbet",
    onSuccess: (response: TransactionReceipt) => {
      updateDiscordChannel(
        items,
        betAmount,
        totalOdds,
        response.transactionHash,
        chainId as number,
        chain?.name as string,
        publicClient,
        betToken.symbol,
        odds
        // isBatch,
        // batchBetAmounts,
        // address as `0x${string}`
      )
      sendGTMEvent({
        event: "betslip_bet_completed",
        user_id: currentUser?.id,
        bet_type: items.length > 1 ? "Combo" : "Single",
        token_approval_required: isApproveRequired,
        bet_count: items.length,
        bet_amount: betAmount,
        bet_payout: totalOddsRef.current * +betAmount,
        // freebet_amount: selectedFreeBet || 0,
        destination_chain: chain?.name,
        destination_token: betToken.symbol,
        destination_wallet_address: address,
        wallet_app_name:
          connector?.name === ConnectorName.Privy ? "privy" : connector?.name,
      })
      openModal("SuccessModal", {
        title: messages.success.title,
      })
      clear()
      // Refetch balances after successful bet placement
      refetchBalances()
    },
    onError: err => {
      if (!isUserRejectedRequestError(err)) {
        openModal("ErrorModal")
      }
      if (isUserRejectedRequestError(err)) {
        sendGTMEvent({
          event: "place_bet_rejected",
          destination_chain: chain?.name,
          destination_wallet_address: address,
          wallet_app_name:
            connector?.name === ConnectorName.Privy ? "privy" : connector?.name,
        })
      }
      console.log("Bet err:", err)
    },
  })

  const isPending = approveTx.isPending || betTx.isPending
  const isProcessing = approveTx.isProcessing || betTx.isProcessing
  const isBalanceRequired = selectedFreebet ? false : !isEnoughBalance

  const isLoading =
    isMaxBetFetching ||
    isOddsFetching ||
    isBalanceFetching ||
    isStatesFetching ||
    isAllowanceLoading ||
    isPending ||
    isProcessing ||
    isRelayerFeeLoading

  const isDisabled =
    isLoading ||
    !address ||
    !isBetAllowed ||
    isBalanceRequired ||
    (isBalanceRequired && !isApproveRequired) ||
    (!+betAmount && !selectedFreebet)

  const rootClassName = cx(
    "flex items-center justify-between py-1 pr-1 border rounded-sm w-full",
    {
      "bg-accent-grey-900 border-accent-grey-950 cursor-not-allowed text-accent-grey-700":
        isDisabled,
      "bg-brand-light-500 text-black border-white/20": !isDisabled,
    }
  )
  const possibleWinClassName = cx(
    "text-caption-12 flex items-center p-2 rounded-min flex-none select-none",
    {
      "bg-accent-grey-800 text-accent-grey-700": isDisabled,
      "bg-accent-grey-1000/60 text-white": !isDisabled,
    }
  )

  const handlePlaceBet = async () => {
    try {
      sendGTMEvent({
        event: "betslip_bet_initiated",
        user_id: currentUser?.id,
        bet_type: items.length > 1 ? "Combo" : "Single",
        token_approval_required: isApproveRequired,
        bet_count: items.length,
        bet_amount: betAmount,
        bet_payout: totalOddsRef.current * +betAmount,
        // freebet_amount: selectedFreeBet || 0,
        destination_chain: chain?.name,
        destination_token: betToken.symbol,
        destination_wallet_address: address,
        wallet_app_name:
          connector?.name === ConnectorName.Privy ? "privy" : connector?.name,
      })
      if (isApproveRequired) {
        setIsAllowanceInitiated(true)
      }

      // Track when user approves the bet transaction
      sendGTMEvent({
        event: "place_bet_approved",
        destination_chain: chain?.name,
        destination_wallet_address: address,
        wallet_app_name:
          connector?.name === ConnectorName.Privy ? "privy" : connector?.name,
      })

      await submit()
    } catch (error) {
      console.error("Error placing bet:", error)
    }
  }

  useEffect(() => {
    const checkAllowance = async () => {
      if (approveTx.isProcessing) {
        sendGTMEvent({
          event: "spend_allowance_approved",
          destination_chain: chain?.name,
          destination_wallet_address: address,
          wallet_app_name:
            connector?.name === ConnectorName.Privy ? "privy" : connector?.name,
        })
        setIsAllowanceInitiated(false)
      }

      if (!approveTx.isPending && !approveTx.isProcessing) {
        sendGTMEvent({
          event: "spend_allowance_rejected",
          destination_chain: chain?.name,
          destination_wallet_address: address,
          wallet_app_name:
            connector?.name === ConnectorName.Privy ? "privy" : connector?.name,
        })
        // setIsAllowanceInitiated(false)
      }
    }

    if (isApproveRequired && isAllowanceInitiated) {
      checkAllowance()
    }
  }, [approveTx.isPending, approveTx.isProcessing])

  return (
    <button
      className={rootClassName}
      onClick={() => void handlePlaceBet()}
      disabled={isDisabled}
    >
      <div className="w-full text-left pl-4 px-1">
        {isLoading ? (
          <Icon className="size-4 mx-auto" name="interface/spinner" />
        ) : (
          <Message
            className="font-bold text-caption-14"
            value={
              isApproveRequired
                ? buttonMessages.approve
                : buttonMessages.placeBet
            }
          />
        )}
      </div>
      <div className={possibleWinClassName}>
        <Message className="mr-1" value={messages.possibleWin} />
        <div className="font-semibold">
          {/* {formatToFixed(
            // isBatch ? getTotalPossibleWin() : totalOddsRef.current * +betAmount,
            2
          )}{' '} */}
          {possibleWin}
          {betToken.symbol}
        </div>
      </div>
    </button>
  )
}

export default BetButton
